import { useCallback, useEffect, useState } from 'react';

import { fetchNodes } from '../api.js';
import type { NodeSummary } from '../api.js';

/**
 * `ready` with an empty array IS the "no nodes yet" state — it is not a
 * separate variant, since there is nothing more to say about it than what the
 * list itself already shows.
 */
export type NodesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; nodes: NodeSummary[] };

export interface UseNodes {
  state: NodesState;
  /**
   * Re-reads the node list. Needed because adding, renaming or deleting a
   * node (via `NodeForm`) changes this list, and neither the list nor the
   * orbit can work that out for itself — shaped exactly like `useOrbit`'s
   * `{ state, reload }` so a caller re-reads both the same way.
   */
  reload: () => Promise<void>;
}

/** Loaded once per Dashboard mount and shared by the list and detail views,
 *  so opening a node never has to re-fetch its title/unit/precision. */
export function useNodes(): UseNodes {
  const [state, setState] = useState<NodesState>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      const nodes = await fetchNodes();
      setState({ kind: 'ready', nodes });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not load your nodes.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, reload: load };
}
